
class BaseMedia extends HTMLElement {
  constructor() {
    super();
    this._abortController = new AbortController();
  }
  connectedCallback() {}
  disconnectedCallback() {
    this._abortController.abort();
  }
  play() {
    this._playerTarget().then((target) => {
      this._playerHandler(target, "play");
    });
  }
  pause() {
    this._playerTarget().then((target) => {
      this._playerHandler(target, "pause");
    });
  }
  _playerTarget() { return Promise.resolve(null); }
  _playerHandler(target, prop) {
    if (target && typeof target[prop] === "function") target[prop]();
  }
}

var onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});

var VideoMedia = class extends BaseMedia {
  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute("autoplay")) {
      const onHostClickOnce = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.play();
      };
      this.addEventListener("click", onHostClickOnce, {
        once: true,
        capture: true,
        signal: this._abortController.signal,
      });
    }

    if (this.hasAttribute("show-play-button") && !this.hasAttribute("autoplay") && !this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(
        document.createRange().createContextualFragment(`
          <slot></slot>
          <svg part="play-button" fill="none" width="48" height="48" viewBox="0 0 48 48">
            <path d="M48 24C48 10.745 37.255 0 24 0S0 10.745 0 24s10.745 24 24 24 24-10.745 24-24Z" fill="#ffffff"/>
            <path d="M18.578 32.629a.375.375 0 0 1-.578-.316V15.687c0-.297.328-.476.578-.316l12.931 8.314c.23.147.23.483 0 .63L18.578 32.63Z" fill="#1a1a1a"/>
            <path d="M24 .5C36.979.5 47.5 11.021 47.5 24S36.979 47.5 24 47.5.5 36.979.5 24 11.021.5 24 .5Z" stroke="#1a1a1a" stroke-opacity=".12"/>
          </svg>
        `)
      );
    }
  }

  play({ restart = false } = {}) {
    if (restart && !this.hasAttribute("host")) {
      const v = this.querySelector("video");
      if (v) v.currentTime = 0;
    }
    super.play();
  }

  _playerTarget() {
    if (this.hasAttribute("host")) {
      this.setAttribute("loaded", "");
      return new Promise(async (resolve) => {
        const templateElement = this.querySelector("template");
        if (templateElement) {
          templateElement.replaceWith(
            templateElement.content.firstElementChild.cloneNode(true)
          );
        }
        const muteVideo = this.hasAttribute("autoplay") || window.matchMedia("screen and (max-width: 999px)").matches;
        const script = document.createElement("script");
        script.type = "text/javascript";

        if (this.getAttribute("host") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(script);
            await new Promise((resolve2) => { script.onload = resolve2; });
          }
          await onYouTubePromise;
          this.setAttribute("can-play", "");
          const player = new YT.Player(this.querySelector("iframe"), {
            events: {
              onReady: () => {
                if (muteVideo) player.mute();
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) this.setAttribute("playing", "");
                else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) this.removeAttribute("playing");
              },
            },
          });
        }

        if (this.getAttribute("host") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js";
            document.head.appendChild(script);
            await new Promise((resolve2) => { script.onload = resolve2; });
          }
          const player = new Vimeo.Player(this.querySelector("iframe"));
          if (muteVideo) player.setMuted(true);
          this.setAttribute("can-play", "");
          player.on("play", () => this.setAttribute("playing", ""));
          player.on("pause", () => this.removeAttribute("playing"));
          player.on("ended", () => this.removeAttribute("playing"));
          resolve(player);
        }
      });
    } else {
      const videoElement = this.querySelector("video");
      this.setAttribute("loaded", "");
      this.setAttribute("can-play", "");

      if (this.hasAttribute("autoplay") && videoElement) {
        setTimeout(() => {
          videoElement.play().catch(() => {});
        }, 100);
      }

      if (videoElement) {
        videoElement.addEventListener("play", () => {
          this.setAttribute("playing", "");
          this.removeAttribute("suspended");
        });
        videoElement.addEventListener("pause", () => {
          if (!videoElement.seeking && videoElement.paused) this.removeAttribute("playing");
        });
      }
      return Promise.resolve(videoElement);
    }
  }

  _playerHandler(target, prop) {
    if (this.getAttribute("host") === "youtube") {
      prop === "play" ? target.playVideo() : target.pauseVideo();
    } else {
      if (prop === "play" && !this.hasAttribute("host")) {
        target.play().catch((error) => {
          if (error && error.name === "NotAllowedError") {
            this.setAttribute("suspended", "");
            if (!this.hasAttribute("autoplay")) {
              target.controls = true;
              const img = target.previousElementSibling?.currentSrc;
              if (img) target.poster = img;
            } else {
              const oncePlay = () => {
                document.removeEventListener("pointerdown", oncePlay, true);
                document.removeEventListener("keydown", oncePlay, true);
                target.play().catch(()=>{});
              };
              document.addEventListener("pointerdown", oncePlay, true);
              document.addEventListener("keydown", oncePlay, true);
            }
          }
        });
      } else {
        if (typeof target[prop] === "function") target[prop]();
      }
    }
  }
};


if (!window.customElements.get("video-media")) {
  window.customElements.define("video-media", VideoMedia);
}

// 自定义组件
// 基础类加前缀
var OtBaseCarousel = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._reloaded = false;
    if (Shopify.designMode) {
      this.closest(".shopify-section")?.addEventListener("shopify:section:select", (event) => this._reloaded = event.detail.load);
    }
    if (this.items.length > 1) {
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target), { animate: !event.detail.load }));
      }
      if (this.hasAttribute("adaptive-height")) {
        this.addEventListener("carousel:settle", this._adjustHeight);
        this._adjustHeight();
      }
      this.addEventListener("carousel:select", this._preloadImages, { signal: this._abortController.signal });
      this.addEventListener("carousel:filter", this._filterItems, { signal: this._abortController.signal });
      this.addEventListener("control:prev", this.previous, { signal: this._abortController.signal });
      this.addEventListener("control:next", this.next, { signal: this._abortController.signal });
      this.addEventListener("control:select", (event) => this.select(event.detail.index), { signal: this._abortController.signal });
    }
    
    // 初始状态类名加前缀 ot-is-selected
    if (this.selectedIndex === 0) {
      this.selectedSlide.classList.add("ot-is-selected");
      this._dispatchEvent("carousel:select", 0);
    } else {
      this.select(this.selectedIndex, { animate: false, force: true });
    }
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  get items() {
    const selector = this.getAttribute("selector") || ":scope > *";
    if (this.hasAttribute("reversed")) {
      return this._items = this._items || Array.from(this.querySelectorAll(selector)).reverse();
    } else {
      return this._items = this._items || Array.from(this.querySelectorAll(selector));
    }
  }

  get visibleItems() {
    return this.items.filter((item) => item.offsetParent !== null);
  }

  get selectedIndex() {
    return this._selectedIndex = this._selectedIndex ?? parseInt(this.getAttribute("initial-index") || 0);
  }

  get selectedSlide() {
    return this.items[this.selectedIndex];
  }

  previous(animate11 = true) {
    const prevIndex = this.visibleItems.indexOf(this.selectedSlide) - 1;
    if (prevIndex >= 0) {
      this.select(this.items.indexOf(this.visibleItems[prevIndex]), { animate: animate11 });
    }
  }

  next(animate11 = true) {
    const nextIndex = this.visibleItems.indexOf(this.selectedSlide) + 1;
    if (nextIndex < this.visibleItems.length) {
      this.select(this.items.indexOf(this.visibleItems[nextIndex]), { animate: animate11 });
    }
  }

  _adjustHeight() {
    if (this.hasAttribute("adaptive-height") && this.selectedSlide.clientHeight !== this.clientHeight) {
      this.style.maxHeight = `${this.selectedSlide.clientHeight}px`;
    }
  }

  _filterItems(event) {
    this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
  }

  _preloadImages() {
    // 预加载逻辑保持不变
  }

  _dispatchEvent(eventName, index) {
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { slide: this.items[index], index } }));
  }
};

// ScrollCarousel 加上 ot- 前缀
var OtScrollCarousel = class extends OtBaseCarousel {
  constructor() {
    super();
    if (window.ResizeObserver) {
      // 使用 throttle 避免性能损耗
      new ResizeObserver(() => this._adjustHeight()).observe(this);
    }
  }

  connectedCallback() {
    // 强制升级其内部的 Dawn 组件，防止局部刷新导致的 JS 失效
    if (window.customElements && window.customElements.upgrade) {
      window.customElements.upgrade(this);
    }
    this._hasPendingProgrammaticScroll = false;
    super.connectedCallback();
    this.addEventListener("scroll", () => this._onCarouselScroll(), { signal: this._abortController.signal });
    this.addEventListener("scrollend", () => this._onScrollSettled(), { signal: this._abortController.signal });
  }

  get itemOffset() {
    return this.visibleItems.length < 2 ? 0 : this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
  }

  select(index, { animate: animate11 = true, force = false } = {}) {
    const indexBeforeChange = this.selectedIndex;
    this._selectedIndex = index;
    this._dispatchEvent("carousel:select", index);

    if (index !== indexBeforeChange || force) {
      const toSlide = this.items[index];
      this._dispatchEvent("carousel:change", index);
      this._transitionTo(toSlide, { animate: animate11 });
    }
  }

  _transitionTo(toSlide, { animate: animate11 = true } = {}) {
    this.items.forEach(item => item.classList.remove("ot-is-selected"));
    toSlide.classList.add("ot-is-selected");

    let scrollAmount = toSlide.offsetLeft;
    // 如果是居中模式，可以微调 scrollAmount
    
    this._hasPendingProgrammaticScroll = animate11;
    this.scrollTo({ left: scrollAmount, behavior: animate11 ? "smooth" : "auto" });
  }

  _onCarouselScroll() {
    if (this._hasPendingProgrammaticScroll) return;
    const newIndex = Math.round(this.scrollLeft / this.itemOffset);
    if (newIndex !== this.selectedIndex && this.items[newIndex]) {
      this._selectedIndex = newIndex;
      this._dispatchEvent("carousel:select", newIndex);
    }
  }

  _onScrollSettled() {
    this.items.forEach((item) => item.classList.remove("ot-is-selected"));
    this.selectedSlide.classList.add("ot-is-selected");
    this._hasPendingProgrammaticScroll = false;
    this._dispatchEvent("carousel:settle", this.selectedIndex);
  }
};

if (!window.customElements.get("ot-scroll-carousel")) {
  window.customElements.define("ot-scroll-carousel", OtScrollCarousel);
}